
import { Splide } from '@splidejs/splide';
import '@splidejs/splide/css';
import { AutoScroll } from '@splidejs/splide-extension-auto-scroll';

const sliders = {
    init() {

        this.heroSlider();
        this.topTxtSlider();
        this.setProdThumbnailsSlider();
        this.setProdGallerySlider();


        this.initOnScroll(
            '.jsProdSlider',
            this.prodSlider.bind(this),
        );

        this.initOnScroll(
            '.jsTextSlider',
            this.textSlider.bind(this),
        );

        this.initOnScroll(
            '.jsSubCatSlider',
            this.subCats.bind(this),
        );

        this.resetSlider();
    },

    resetSlider() {
        window.addEventListener('reset', () => {
            this.setProdThumbnailsSlider();
            this.setProdGallerySlider();
        });
    },

    initOnScroll(
        selector: string,
        callback: (element: HTMLElement) => void,
    ) {
        const elements = document.querySelectorAll(
            selector,
        ) as NodeListOf<HTMLElement>;

        if (!elements.length) return;

        if (!('IntersectionObserver' in window)) {
            elements.forEach((element) => {
                if (
                    element.dataset.sliderInitialized === 'true'
                ) {
                    return;
                }

                element.dataset.sliderInitialized = 'true';

                callback(element);
            });

            return;
        }

        const observer = new IntersectionObserver(
            (entries, currentObserver) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;

                    const element = entry.target as HTMLElement;

                    if (
                        element.dataset.sliderInitialized ===
                        'true'
                    ) {
                        currentObserver.unobserve(element);
                        return;
                    }

                    element.dataset.sliderInitialized = 'true';

                    currentObserver.unobserve(element);

                    callback(element);
                });
            },
            {
                root: null,
                rootMargin: '200px 0px',
                threshold: 0,
            },
        );

        elements.forEach((element) => {
            if (
                element.dataset.sliderInitialized === 'true'
            ) {
                return;
            }

            observer.observe(element);
        });
    },

    heroSlider() {
        const sliderElements = document.querySelectorAll(
            '.jsHeroSlider',
        ) as NodeListOf<HTMLElement>;

        const loadSlideAssets = (
            slide: HTMLElement | null,
        ) => {
            if (
                !slide ||
                slide.dataset.assetsLoaded === 'true'
            ) {
                return;
            }

            const picture = slide.querySelector('picture');

            if (!picture) return;

            const sources = picture.querySelectorAll(
                'source[data-srcset]',
            );

            const image = picture.querySelector(
                'img[data-src]',
            ) as HTMLImageElement | null;

            sources.forEach((source) => {
                const dataSrcset =
                    source.getAttribute('data-srcset');

                if (!dataSrcset) return;

                source.setAttribute(
                    'srcset',
                    dataSrcset,
                );

                source.removeAttribute('data-srcset');
            });

            if (image) {
                const dataSrc =
                    image.getAttribute('data-src');

                if (dataSrc) {
                    image.setAttribute('src', dataSrc);
                    image.removeAttribute('data-src');
                }
            }

            slide.dataset.assetsLoaded = 'true';
        };

        sliderElements.forEach((element) => {
            if (
                element.dataset.sliderInitialized === 'true'
            ) {
                return;
            }

            element.dataset.sliderInitialized = 'true';

            const slider = new Splide(element, {
                type: 'slide',
                rewind: true,
                perPage: 1,
                pagination: true,
                arrows: true,
            });

            slider.on('mounted move', () => {
                const activeSlide = element.querySelector(
                    '.splide__slide.is-active',
                ) as HTMLElement | null;

                const previousSlide = element.querySelector(
                    '.splide__slide.is-prev',
                ) as HTMLElement | null;

                const nextSlide = element.querySelector(
                    '.splide__slide.is-next',
                ) as HTMLElement | null;

                loadSlideAssets(activeSlide);
                loadSlideAssets(previousSlide);
                loadSlideAssets(nextSlide);
            });

            slider.mount();
        });
    },

    topTxtSlider() {
        const sliderElements = document.querySelectorAll(
            '.jsTopTxt',
        ) as NodeListOf<HTMLElement>;

        sliderElements.forEach((element) => {
            if (
                element.dataset.sliderInitialized === 'true'
            ) {
                return;
            }

            element.dataset.sliderInitialized = 'true';

            new Splide(element, {
                perPage: 1,
                type: 'fade',
                speed: 2000,
                arrows: false,
                pagination: false,
                cover: false,
                autoplay: true,
                rewind: true,
                reducedMotion: {
                    interval: 4000,
                    speed: 800,
                    autoplay: true,
                },
            }).mount();
        });
    },

    prodSlider(element: HTMLElement) {
        new Splide(element, {
            type: 'slide',
            arrows: false,
            gap: 25,
            perPage: 5,
            pagination: true,
            rewind: true,

            breakpoints: {
                1200: {
                    perPage: 2,
                    perMove: 1,
                    padding: {
                        right: '40%',
                    },
                },

                767: {
                    perPage: 1,
                    perMove: 1,
                    padding: {
                        right: '40%',
                    },
                },
            },
        }).mount();
    },

    textSlider(element: HTMLElement) {
        new Splide(element, {
            type: 'loop',
            perPage: 4,
            pagination: false,
            arrows: false,
            autoWidth: true,
            gap: 50,

            autoScroll: {
                speed: -1,
            },

            breakpoints: {
                768: {
                    perPage: 3,
                    gap: 35,
                },

                500: {
                    perPage: 1,
                    padding: {
                        right: '30%',
                    },
                },
            },
        }).mount({
            AutoScroll,
        });
    },

    subCats(element: HTMLElement) {
        new Splide(element, {
            type: 'slide',
            arrows: true,
            gap: 30,
            perPage: 4,
            rewind: true,
            pagination: false,

            breakpoints: {
                1200: {
                    arrows: false,
                    pagination: true,
                    gap: 20,
                    perPage: 3,
                    perMove: 1,
                },

                767: {
                    arrows: false,
                    pagination: true,
                    gap: 15,
                    padding: {
                        right: '20%',
                    },
                    perPage: 2,
                    perMove: 1,
                },
            },
        }).mount();
    },

    setProdThumbnailsSlider() {
        const productMainImages =
            document.getElementById('mainImage');

        const thumbnails =
            document.getElementById('thumbnails');

        if (!productMainImages || !thumbnails) return;

        if (
            productMainImages.dataset.sliderInitialized ===
                'true' ||
            thumbnails.dataset.sliderInitialized === 'true'
        ) {
            return;
        }

        productMainImages.dataset.sliderInitialized = 'true';
        thumbnails.dataset.sliderInitialized = 'true';

        const slideCount =
            productMainImages.querySelectorAll(
                '.splide__slide',
            ).length;

        const main = new Splide(productMainImages, {
            type: 'fade',
            rewind: true,
            pagination: false,
            arrows: slideCount > 4,
        });

        const thumbnailsNav = new Splide(thumbnails, {
            type: 'slide',
            direction: 'ttb',
            isNavigation: true,
            pagination: false,
            height: '560px',
            fixedHeight: 140,
            arrows: slideCount > 4,
            gap: 0,
        });

        main.sync(thumbnailsNav);

        main.mount();
        thumbnailsNav.mount();
    },

    setProdGallerySlider() {
        const productMainImages =
            document.getElementById(
                'mainImageGallery',
            );

        const thumbnails =
            document.getElementById(
                'thumbnailsGallery',
            );

        if (!productMainImages || !thumbnails) return;

        if (
            productMainImages.dataset.sliderInitialized ===
                'true' ||
            thumbnails.dataset.sliderInitialized === 'true'
        ) {
            return;
        }

        productMainImages.dataset.sliderInitialized = 'true';
        thumbnails.dataset.sliderInitialized = 'true';

        const slideCount =
            productMainImages.querySelectorAll(
                '.splide__slide',
            ).length;

        const main = new Splide(productMainImages, {
            type: 'fade',
            rewind: true,
            pagination: false,
            arrows: slideCount > 4,
        });

        const thumbnailsNav = new Splide(thumbnails, {
            type: 'slide',
            direction: 'ttb',
            isNavigation: true,
            pagination: false,
            height: '560px',
            fixedHeight: 140,
            arrows: slideCount > 4,
            gap: 0,
        });

        main.sync(thumbnailsNav);

        main.mount();
        thumbnailsNav.mount();
    },
};

export default sliders;

